import { MenuOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import { Button, Divider, Tooltip } from 'antd'
import { NavMenu } from 'components/Navigation'
import Feedback from 'features/users/components/Feedback'
import { useDisclosure } from 'hooks/useDisclosure'
import { FC, useEffect } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { useChatMenu } from 'hooks/useChatMenu'
import { TOUR_SET_NAV_DRAWER_OPEN_EVENT } from 'features/spaces/components/LearnerProductTour'
import NewChatIcon from 'assets/new-chat-icon'

type Props = {
  hasSubMenu?: boolean
}
const AppLayout: FC<Props> = ({ hasSubMenu = false }) => {
  const { open, close, isOpen } = useDisclosure()
  const params = useParams()
  const { handleCreateChat, isAIConfigSet, isCreateChatLoading, scopeId } = useChatMenu()

  // The learner product tour (in the HomePage tree) reveals the chat history for one step by opening
  // this mobile nav Drawer through a CustomEvent — the Drawer's open state lives here, not in the tour.
  useEffect(() => {
    const onSetDrawerOpen = (e: Event) => {
      const { open: shouldOpen } = (e as CustomEvent<{ open: boolean }>).detail
      if (shouldOpen) open()
      else close()
    }
    window.addEventListener(TOUR_SET_NAV_DRAWER_OPEN_EVENT, onSetDrawerOpen)
    return () => window.removeEventListener(TOUR_SET_NAV_DRAWER_OPEN_EVENT, onSetDrawerOpen)
  }, [open, close])

  const shouldShowCreateChatButton = !!params.workspaceId && !!params.spaceId && !!scopeId && isAIConfigSet
  return (
    <div className="relative z-0 flex-col flex h-full w-full overflow-hidden xl:flex-row ">
      <div className="flex xl:hidden absolute top-2 right-2 z-20 md:mr-2 md:mt-2 ">{hasSubMenu && <Feedback />}</div>
      <NavMenu open={isOpen} onClose={close} hasSubMenu={hasSubMenu} />
      <div className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden">
        <div className="sticky top-0 z-10 flex min-h-14 bg-background items-center justify-start px-4 md:px-8 xl:hidden w-full">
          <Button type="text" onClick={open} className="xl:hidden items-center flex h-10 -ml-3 mr-3">
            {!open ? <MenuUnfoldOutlined /> : <MenuOutlined />}
          </Button>
        </div>
        <Divider className="xl:hidden m-0" />
        <div className="relative h-full w-full flex-1 overflow-auto transition-width">
          <div className="hidden xl:flex xl:flex-row xl:gap-2 absolute top-2 right-2 z-20 md:mr-2 md:mt-2 ">
            {hasSubMenu && <Feedback catchEmitter={true} />}
            {shouldShowCreateChatButton && (
              <Tooltip title="New chat">
                <Button
                  size="middle"
                  disabled={!isAIConfigSet}
                  icon={<NewChatIcon />}
                  loading={isCreateChatLoading}
                  onClick={handleCreateChat}
                />
              </Tooltip>
            )}
          </div>
          <Outlet context={useOutletContext()} />
        </div>
      </div>
    </div>
  )
}

export default AppLayout
